import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { Todo } from '@signa/shared';
import { useTodoStore } from '../../stores/todo-store';
import { useAppStore } from '../../stores/appStore';
import { PageHeader } from '../shared-ui/PageHeader';

/**
 * Todos - handoff-style list with always-visible quick-add, grouped by due.
 * Quick-add routes through createTodo(), which does NLP date parsing
 * server-side ("Call Mom tomorrow" to title + dueDate).
 */

export function TodosView() {
  const todos = useTodoStore((s) => s.todos);
  const createTodo = useTodoStore((s) => s.createTodo);
  const updateTodo = useTodoStore((s) => s.updateTodo);
  const setActiveTodoId = useAppStore((s) => s.setActiveTodoId);

  const [draft, setDraft] = useState('');
  const [adding, setAdding] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // Global `/` shortcut focuses this input.
  useEffect(() => {
    const onFocus = () => inputRef.current?.focus();
    window.addEventListener('signa:focus-todo-quickadd', onFocus);
    return () => window.removeEventListener('signa:focus-todo-quickadd', onFocus);
  }, []);

  const { overdue, today, later, noDate, done } = useMemo(() => groupTodos(todos), [todos]);

  const open = todos.filter((t) => !t.completedAt).length;
  const dueToday = today.length + overdue.length;

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      const text = draft.trim();
      if (!text || adding) return;
      setAdding(true);
      try {
        await createTodo(text);
        setDraft('');
        inputRef.current?.focus();
      } catch {
        // store surfaces errors via toast already
      } finally {
        setAdding(false);
      }
    },
    [draft, adding, createTodo]
  );

  const onToggle = useCallback(
    (todo: Todo) => {
      updateTodo(todo.id, {
        completedAt: todo.completedAt ? undefined : new Date().toISOString(),
      });
    },
    [updateTodo]
  );

  return (
    <div style={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
      <div
        style={{
          maxWidth: 720,
          margin: '0 auto',
          padding: '40px 36px 120px',
          width: '100%',
        }}
      >
        <PageHeader
          title="Todos"
          subtitle={`${open} open / ${dueToday} due today`}
        />

        <QuickAdd
          value={draft}
          onChange={setDraft}
          onSubmit={handleSubmit}
          inputRef={inputRef}
          disabled={adding}
        />

        <Group label="Overdue" todos={overdue} onOpen={setActiveTodoId} onToggle={onToggle} accentOverdue />
        <Group label="Today" todos={today} onOpen={setActiveTodoId} onToggle={onToggle} />
        <Group label="This week" todos={later} onOpen={setActiveTodoId} onToggle={onToggle} />
        <Group label="Someday" todos={noDate} onOpen={setActiveTodoId} onToggle={onToggle} />
        {done.length > 0 && (
          <Group label="Completed" todos={done} onOpen={setActiveTodoId} onToggle={onToggle} dim />
        )}

        {todos.length === 0 && <EmptyState />}
      </div>
    </div>
  );
}

// Quick add

function QuickAdd({
  value,
  onChange,
  onSubmit,
  inputRef,
  disabled,
}: {
  value: string;
  onChange: (v: string) => void;
  onSubmit: (e: React.FormEvent) => void;
  inputRef: React.RefObject<HTMLInputElement>;
  disabled: boolean;
}) {
  const [focused, setFocused] = useState(false);
  return (
    <form
      onSubmit={onSubmit}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '10px 14px 10px 12px',
        background: 'var(--bg-elevated)',
        border: `1.5px ${focused || value ? 'solid' : 'dashed'} ${
          focused ? 'var(--accent)' : 'var(--border)'
        }`,
        borderRadius: 10,
        marginBottom: 28,
        transition: 'border-color 150ms ease',
      }}
    >
      <span
        aria-hidden
        style={{
          width: 16,
          height: 16,
          borderRadius: 4,
          border: '1.5px solid var(--text-tertiary)',
          flexShrink: 0,
        }}
      />
      <input
        ref={inputRef}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        placeholder="Capture a todo... (e.g. 'Follow up with Sarah re: Q2 proposal')"
        disabled={disabled}
        style={{
          flex: 1,
          background: 'transparent',
          border: 'none',
          outline: 'none',
          color: 'var(--text-primary)',
          fontFamily: 'var(--font-ui)',
          fontSize: 14,
          padding: '6px 0',
        }}
      />
      <button
        type="submit"
        disabled={disabled || value.trim().length === 0}
        style={{
          padding: '5px 12px',
          background: value.trim() ? 'var(--accent)' : 'transparent',
          color: value.trim() ? 'var(--text-inverse)' : 'var(--text-tertiary)',
          border: 'none',
          borderRadius: 6,
          fontFamily: 'var(--font-ui)',
          fontSize: 12.5,
          fontWeight: 500,
          cursor: value.trim() ? 'pointer' : 'default',
          transition: 'all 150ms ease',
        }}
      >
        Add
      </button>
    </form>
  );
}

// Group

function Group({
  label,
  todos,
  onOpen,
  onToggle,
  dim = false,
  accentOverdue = false,
}: {
  label: string;
  todos: Todo[];
  onOpen: (id: string) => void;
  onToggle: (t: Todo) => void;
  dim?: boolean;
  accentOverdue?: boolean;
}) {
  if (todos.length === 0) return null;
  return (
    <section style={{ marginBottom: 28, opacity: dim ? 0.6 : 1 }}>
      <div
        style={{
          fontFamily: 'var(--font-mono)',
          fontSize: 10.5,
          letterSpacing: '1.3px',
          textTransform: 'uppercase',
          color: accentOverdue ? 'var(--accent)' : 'var(--text-tertiary)',
          marginBottom: 10,
        }}
      >
        {label} / {todos.length}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {todos.map((t) => (
          <TodoCard key={t.id} todo={t} onOpen={() => onOpen(t.id)} onToggle={() => onToggle(t)} />
        ))}
      </div>
    </section>
  );
}

function TodoCard({
  todo,
  onOpen,
  onToggle,
}: {
  todo: Todo;
  onOpen: () => void;
  onToggle: () => void;
}) {
  const done = !!todo.completedAt;
  return (
    <div
      onClick={onOpen}
      role="button"
      style={{
        display: 'flex',
        gap: 12,
        padding: '12px 14px',
        background: 'var(--bg-elevated)',
        border: '1px solid var(--border)',
        borderRadius: 10,
        cursor: 'pointer',
        alignItems: 'flex-start',
        transition: 'border-color 150ms ease',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.borderColor = 'var(--accent)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderColor = 'var(--border)';
      }}
    >
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onToggle();
        }}
        aria-label={done ? 'Mark incomplete' : 'Mark complete'}
        style={{
          width: 16,
          height: 16,
          borderRadius: 4,
          border: `1.5px solid ${done ? 'var(--accent)' : 'var(--text-tertiary)'}`,
          background: done ? 'var(--accent)' : 'transparent',
          marginTop: 2,
          padding: 0,
          flexShrink: 0,
          cursor: 'pointer',
          display: 'grid',
          placeItems: 'center',
        }}
      >
        {done && (
          <svg
            width="10"
            height="10"
            viewBox="0 0 16 16"
            fill="none"
            stroke="var(--text-inverse)"
            strokeWidth={2.2}
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="m3 8.2 3.2 3.3L13 4.5" />
          </svg>
        )}
      </button>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontSize: 14.5,
            color: done ? 'var(--text-tertiary)' : 'var(--text-primary)',
            textDecoration: done ? 'line-through' : 'none',
          }}
        >
          {todo.title}
        </div>
        {(todo.dueDate || (todo.labels ?? []).length > 0) && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              marginTop: 6,
              fontFamily: 'var(--font-mono)',
              fontSize: 10.5,
              color: 'var(--text-tertiary)',
              letterSpacing: '0.3px',
            }}
          >
            {todo.dueDate && (
              <span
                style={{
                  color: dueTone(todo.dueDate),
                }}
              >
                {formatDue(todo.dueDate)}
              </span>
            )}
            {(todo.labels ?? []).slice(0, 3).map((l) => (
              <span key={l} style={{ opacity: 0.8 }}>
                #{l}
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// Helpers

function groupTodos(todos: Todo[]): {
  overdue: Todo[];
  today: Todo[];
  later: Todo[];
  noDate: Todo[];
  done: Todo[];
} {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const endOfWeek = new Date(today);
  endOfWeek.setDate(today.getDate() + 7);

  const overdue: Todo[] = [];
  const todayArr: Todo[] = [];
  const later: Todo[] = [];
  const noDate: Todo[] = [];
  const done: Todo[] = [];

  todos.forEach((t) => {
    if (t.completedAt) {
      done.push(t);
      return;
    }
    if (!t.dueDate) {
      noDate.push(t);
      return;
    }
    const d = new Date(t.dueDate);
    d.setHours(0, 0, 0, 0);
    if (d.getTime() < today.getTime()) overdue.push(t);
    else if (d.getTime() === today.getTime()) todayArr.push(t);
    else if (d.getTime() <= endOfWeek.getTime()) later.push(t);
    else noDate.push(t); // Far future bucket in Someday.
  });

  return { overdue, today: todayArr, later, noDate, done };
}

function formatDue(iso: string): string {
  const d = new Date(iso);
  if (!Number.isFinite(d.getTime())) return '';
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const diff = Math.round((d.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
  if (diff === 0) return 'Today';
  if (diff === 1) return 'Tomorrow';
  if (diff === -1) return 'Yesterday';
  if (diff < 0) return `${Math.abs(diff)}d overdue`;
  if (diff < 7) return d.toLocaleDateString([], { weekday: 'long' });
  return d.toLocaleDateString([], { month: 'short', day: 'numeric' });
}

function dueTone(iso: string): string {
  const d = new Date(iso);
  if (!Number.isFinite(d.getTime())) return 'var(--text-tertiary)';
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const diff = Math.round((d.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
  if (diff < 0) return 'var(--accent)';
  if (diff === 0) return 'var(--accent)';
  return 'var(--text-tertiary)';
}

function EmptyState() {
  return (
    <div
      style={{
        padding: '60px 24px',
        textAlign: 'center',
        color: 'var(--text-tertiary)',
        fontSize: 13,
        border: '1px dashed var(--border)',
        borderRadius: 12,
        marginTop: 16,
      }}
    >
      No todos yet. Capture one in the box above - "Follow up with Sarah re: Q2 proposal" gets
      parsed automatically.
    </div>
  );
}
